import React, { useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import API from '../../services/API'

// Shared building blocks for module doc pages. Originally lived only in
// CommandControlComponents.jsx; promoted here so every module (Clinical
// Records, Event Planning, Fleet & Equipment, Governance, etc.) can use the
// same page/section/screenshot pattern instead of hand-rolled MUI Stacks.

export const Page = ({ title, intro, children }) => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">{title}</Typography>
      {intro && <Typography>{intro}</Typography>}
    </Stack>
    {children}
  </Stack>
)

export const Section = ({ title, children }) => (
  <Stack gap={1}>
    <Typography variant="h6" fontWeight={900}>
      {title}
    </Typography>
    {children}
  </Stack>
)

export const Bullets = ({ children }) => (
  <Box component="ul" sx={{ pl: 3, m: 0 }}>
    {children}
  </Box>
)

export const Bullet = ({ children }) => (
  <Typography component="li" sx={{ mb: 0.75 }}>
    {children}
  </Typography>
)

const getImageFilename = (value) => {
  if (!value) return null

  try {
    const parsed = new URL(value)
    return decodeURIComponent(parsed.pathname.replace(/^\/+/, ''))
  } catch {
    return value
  }
}

// Renders a real screenshot when a filename/src/url is supplied (fetched via
// /kbdocs/image). With no image reference it falls back to an info callout
// showing the caption text, which is how "TODO: INSERT_SCREENSHOT" style
// placeholders render until an image is added.
export const Screenshot = ({ alt, children, filename, src, url }) => {
  const [imageUrl, setImageUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isZoomed, setIsZoomed] = useState(false)
  const imageFilename = getImageFilename(filename || src || url)

  useEffect(() => {
    let isMounted = true

    if (!imageFilename) {
      setImageUrl(null)
      setIsLoading(false)
      setHasError(false)
      return undefined
    }

    setIsLoading(true)
    setHasError(false)

    API.post('/kbdocs/image', { filename: imageFilename })
      .then((res) => {
        if (!isMounted) return

        if (res.status >= 200 && res.status < 300 && res.data) {
          setImageUrl(res.data)
        } else {
          setImageUrl(null)
          setHasError(true)
        }
      })
      .catch(() => {
        if (!isMounted) return
        setImageUrl(null)
        setHasError(true)
      })
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [imageFilename])

  if (imageFilename) {
    return (
      <Box component="figure" sx={{ m: 0 }}>
        {isLoading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              minHeight: 180,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CircularProgress size={24} />
          </Stack>
        )}
        {!isLoading && imageUrl && !hasError && (
          <Box
            component="img"
            src={imageUrl}
            onClick={() => setIsZoomed(true)}
            // The presigned URL is issued whether or not the object exists in
            // S3, so a missing image only surfaces here - without this the
            // reader gets a broken-image icon instead of the warning.
            onError={() => {
              setImageUrl(null)
              setHasError(true)
            }}
            alt={
              alt || (typeof children === 'string' ? children : 'Screenshot')
            }
            sx={{
              display: 'block',
              cursor: 'zoom-in',
              // Wide screenshots still fill the column, but tall (portrait
              // and mobile-app) captures are capped so they don't take over
              // the page — the height cap is what decides their width.
              width: 'auto',
              height: 'auto',
              maxWidth: '100%',
              maxHeight: { xs: '65vh', md: 620 },
              mx: 'auto',
              boxSizing: 'border-box',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        )}
        {!isLoading && hasError && (
          <Alert severity="warning" variant="outlined">
            Screenshot could not be loaded.
          </Alert>
        )}
        {children && (
          <Typography
            component="figcaption"
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1 }}
          >
            {children}
          </Typography>
        )}

        {/* Capping tall screenshots shrinks them a lot on a phone, so tapping
            one opens it full size and scrollable. */}
        <Dialog
          open={isZoomed}
          onClose={() => setIsZoomed(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              m: { xs: 1, sm: 4 },
              width: '100%',
              maxWidth: { xs: '100%', sm: 1200 },
              maxHeight: { xs: '96vh', sm: '90vh' },
            },
          }}
        >
          <Box sx={{ position: 'relative', bgcolor: '#0F172A' }}>
            <IconButton
              onClick={() => setIsZoomed(false)}
              aria-label="Close screenshot"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                bgcolor: 'rgba(15,23,42,0.55)',
                '&:hover': { bgcolor: 'rgba(15,23,42,0.75)' },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>

            <Box sx={{ maxHeight: { xs: '92vh', sm: '86vh' }, overflow: 'auto' }}>
              <Box
                component="img"
                src={imageUrl}
                alt={
                  alt ||
                  (typeof children === 'string' ? children : 'Screenshot')
                }
                // On a phone the image is blown up past the screen and the box
                // scrolls, so detail is actually readable. Screenshots are
                // captured at varying pixel densities, so this zooms relative
                // to the screen rather than to the file's own size.
                sx={{
                  display: 'block',
                  width: { xs: '260%', sm: '100%' },
                  maxWidth: { xs: 'none', sm: '100%' },
                  height: 'auto',
                }}
              />
            </Box>
          </Box>
        </Dialog>
      </Box>
    )
  }

  return (
    <Alert severity="info" variant="outlined">
      {children}
    </Alert>
  )
}

export const Info = ({ children, title = 'Good to know' }) => (
  <Alert
    icon={<InfoRoundedIcon />}
    severity="info"
    sx={{
      borderRadius: 3,
      border: '1px solid rgba(35,154,164,0.18)',
      bgcolor: 'rgba(35,154,164,0.07)',

      '& p': {
        mb: 0,
      },
    }}
  >
    <AlertTitle color="rgba(35,154,164)">{title}</AlertTitle>

    {children}
  </Alert>
)

export const Warning = ({ children, title = 'Important' }) => (
  <Alert
    icon={<WarningAmberRoundedIcon />}
    severity="warning"
    sx={{
      my: 3,
      borderRadius: 3,
      border: '1px solid rgba(245,158,11,0.25)',
      bgcolor: 'rgba(245,158,11,0.08)',
      '& p': {
        mb: 0,
      },
    }}
  >
    <Typography fontWeight={900} mb={0.5}>
      {title}
    </Typography>
    {children}
  </Alert>
)

export const Success = ({ children, title = 'Complete' }) => (
  <Alert
    icon={<CheckCircleRoundedIcon />}
    severity="success"
    sx={{
      my: 3,
      borderRadius: 3,
      border: '1px solid rgba(22,163,74,0.20)',
      bgcolor: 'rgba(22,163,74,0.07)',
      '& p': {
        mb: 0,
      },
    }}
  >
    <Typography fontWeight={900} mb={0.5}>
      {title}
    </Typography>
    {children}
  </Alert>
)

export const Step = ({ number, title, children }) => (
  <Stack direction="row" spacing={2} sx={{ my: 3 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 2,
        flexShrink: 0,
        bgcolor: '#239AA4',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 900,
      }}
    >
      {number}
    </Box>

    <Box>
      <Typography fontWeight={950} color="#1A2230" mb={0.75}>
        {title}
      </Typography>
      <Box>{children}</Box>
    </Box>
  </Stack>
)

// DocLink lives in ./DocLink.jsx (handles the docs subdomain correctly) —
// import it from there, not from here.

export const FeatureBadge = ({ children, icon }) => (
  <Chip
    label={children}
    icon={<SvgIcon component={icon} fontSize="small" />}
    size="small"
    sx={{
      my: 1,
      fontWeight: 900,
      color: '#239AA4',
      bgcolor: 'rgba(35,154,164,0.10)',
      '& .MuiChip-icon': {
        color: '#239AA4',
      },
    }}
  />
)

export const ActionBadge = ({ children, icon }) => (
  <Chip
    label={children}
    icon={<SvgIcon component={icon} fontSize="small" />}
    size="small"
    sx={{
      my: 1,
      fontWeight: 800,
      color: '#1D4ED8',
      bgcolor: 'rgba(37,99,235,0.10)',
      border: '1px solid rgba(37,99,235,0.14)',
      borderRadius: 1,
      '& .MuiChip-icon': {
        color: '#1D4ED8',
      },
    }}
  />
)

export const SuccessBadge = ({ children, icon }) => (
  <Chip
    label={children}
    icon={<SvgIcon component={icon} fontSize="small" />}
    size="small"
    sx={{
      my: 1,
      fontWeight: 800,
      color: '#15803D',
      bgcolor: 'rgba(34,197,94,0.10)',
      border: '1px solid rgba(37,99,235,0.14)',
      borderRadius: 1,
      '& .MuiChip-icon': {
        color: '#15803D',
      },
    }}
  />
)
export const WarningBadge = ({ children, icon }) => (
  <Chip
    label={children}
    icon={icon && <SvgIcon component={icon} fontSize="small" />}
    size="small"
    sx={{
      my: 1,
      fontWeight: 800,
      color: '#B45309',
      bgcolor: 'rgba(245,158,11,0.12)',
      border: '1px solid rgba(37,99,235,0.14)',
      borderRadius: 1,
      '& .MuiChip-icon': {
        color: '#B45309',
      },
    }}
  />
)
export const DangerBadge = ({ children, icon }) => (
  <Chip
    label={children}
    icon={<SvgIcon component={icon} fontSize="small" />}
    size="small"
    sx={{
      my: 1,
      fontWeight: 800,
      color: '#B91C1C',
      bgcolor: 'rgba(239,68,68,0.10)',
      border: '1px solid rgba(37,99,235,0.14)',
      borderRadius: 1,
      '& .MuiChip-icon': {
        color: '#B91C1C',
      },
    }}
  />
)
